import { useEffect, useState, Suspense, lazy } from "react";
import "./App.css";
import InvalidPage from "./components/InvalidPage";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import WebView from "./components/WebView";
import MultiExpConfigProvider from "./context/MultiExpConfigProvider";
import PlayerGrid from "./components/PlayerGrid";
import Loader from "../src/molecules/SpinningLoader";
import FirstPartyDataRoute from "./components/FirstPartyDataRoute";

// Lazy load heavy components
const CameraApp = lazy(() => import("./pages/SnapCamera/CameraApp"));
const SnapCamera = lazy(() => import("./pages/SnapCamera/SnapCamera"));
const ChatbotPage = lazy(() => import("./pages/ChatBot/ChatbotPage"));
const Videos = lazy(() => import("./pages/SnapCamera/Video"));
const ModelViewer = lazy(() => import("./pages/SnapCamera/ModelViewer"));
const MultiExp = lazy(() => import("./pages/SnapCamera/MultiExp"));

// Loading component for lazy-loaded routes
const RouteLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
  </div>
);


function App() {
  const [sessionId, setSessionId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const DISPLAY_MESSAGE =
    "Scan the QR code on your phone to activate SpeedShotX.";






  useEffect(() => {
    let session = sessionStorage.getItem("sessionId");

    const handleResize = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isTablet =
        /ipad|android(?!.*mobi)|tablet|playbook|silk|kindle|nexus 7|nexus 10/.test(
          userAgent
        ) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

      setIsMobile(window.innerWidth <= 1024 || isTablet);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    if (!session) {
      session = uuidv4();
      sessionStorage.setItem("sessionId", session);
    }
    setSessionId(session);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Detect PWA install availability (Android)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault(); // Prevent automatic prompt
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  // Detect iOS (Safari) for manual install instructions
  useEffect(() => {
    const ua = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(ua);
    const isSafari = /safari/.test(ua) && !/chrome/.test(ua);
    const isStandalone = window.matchMedia(
      "(display-mode: standalone)"
    ).matches;

    if (isIosDevice && isSafari && !isStandalone) {
      setIsIos(true);
      setShowInstallButton(true);
    }
  }, []);

  // Handle Install Button Click (Android)
  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === "accepted") {
          console.log("User accepted the install prompt");
        }
        setDeferredPrompt(null);
        setShowInstallButton(false);
      });
    }
  };

  return (
    <div className="App">
      {/* {showInstallButton && (
        <div className="install-prompt">
          {isIos ? (
            <p>
              Tap <strong>Share</strong> &gt;{" "}
              <strong>Add to Home Screen</strong> to install.
            </p>
          ) : (
            <button onClick={handleInstallClick}>Add to Home Screen</button>
          )}
        </div>
      )} */}

      <Router>
        <Routes>
          <Route
            exact
            path="/ar/lens"
            element={
              isMobile ? (
                <Suspense fallback={<Loader />}>
                  <SnapCamera />
                </Suspense>
              ) : (
                <WebView />
              )
            }
          />

          <Route
            exact
            path="/ar/multiexp"
            element={
              <MultiExpConfigProvider>
                <Suspense fallback={<Loader />}>
                  <MultiExp />
                </Suspense>
              </MultiExpConfigProvider>
            }
          />
          <Route
            exact
            path="/ar/camera"
            element={
              isMobile ? (
                <Suspense fallback={<Loader />}>
                  <CameraApp />
                </Suspense>
              ) : (
                <WebView message={DISPLAY_MESSAGE} />
              )
            }
          />
          <Route
            exact
            path="/ar/videos"
            element={
              isMobile ? (
                <Suspense fallback={<Loader />}>
                  <Videos />
                </Suspense>
              ) : (
                <WebView message={DISPLAY_MESSAGE} />
              )
            }
          />
          <Route
            exact
            path="/ar/models"
            element={
              isMobile ? (
                <Suspense fallback={<Loader />}>
                  <ModelViewer />
                </Suspense>
              ) : (
                <WebView message={DISPLAY_MESSAGE} />
              )
            }
          />
          <Route
            exact
            path="/ar/chatbot"
            element={
              <Suspense fallback={<Loader />}>
                <ChatbotPage />
              </Suspense>
            }
          />
          <Route exact path="/ar/players" element={<PlayerGrid />} />
          <Route
            exact
            path="/ar/form"
            element={
              isMobile ? <FirstPartyDataRoute /> : <WebView message={DISPLAY_MESSAGE} />
            }
          />
          <Route path="*" element={<InvalidPage />} />
        </Routes>
      </Router>
    </div>
  );
}

export default App;
